import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { AlertDialog, Callout, TopNavigation } from "@hamlak/ui";
import logoUrl from "@hamlak/ui/brand/logo.svg";
import defaultHeroUrl from "@/assets/login-hero-default.webp";
import { platform } from "@/platform";
import { backOr } from "@/app/navigation";
import { t } from "@/i18n/ko";
import { formatDateTimeDot } from "@/lib/formatDate";
import { useConfigStore } from "@/stores/configStore";
import { useUiStore } from "@/stores/uiStore";
import { GoogleButton } from "@/components/GoogleButton";
import { AppleButton } from "@/components/AppleButton";
import { OpenInBrowserPage } from "./OpenInBrowserPage";
import { useSocialLogin } from "./useSocialLogin";
import { paragraphs } from "@/features/system/NoticeDialogs";
import "./auth.css";

/**
 * F01-02 Google · F01-02a Apple 로그인(시안 44:849, AC0-94): 플롯 커버 히어로(520, 하단 그라데이션) + 로고 + 플롯 태그라인 + "그 철벽…" + 가입 선물 한 줄(pricing.welcome) + GoogleButton + AppleButton + 동의 고지
 * + REJOIN_BLOCKED Callout + ACCOUNT_BLOCKED 다이얼로그. 로그인 요청 시트는 없고 모든 진입이 이 페이지. 플롯 맥락이 없으면 기본 히어로 = 시안 F01-02 기본 이미지 케이스(758:2)의 캐릭터 모음 390×520(756:6036, AC0-256 — AC0-168 의 5:3 띠에서 교체).
 * 웹 인앱 브라우저면 OpenInBrowserPage.
 */
export function LoginPage() {
  if (platform.clientKind === "web" && platform.device.isInAppBrowser()) return <OpenInBrowserPage />;
  return <LoginView />;
}

function LoginView() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const google = useSocialLogin("google");
  const apple = useSocialLogin("apple");
  const rejoinDays = useConfigStore((s) => s.settings?.pricing.rejoinBlockDays);
  const welcomeAmount = useConfigStore((s) => s.settings?.pricing.welcome);
  const setReturnTo = useUiStore((s) => s.setReturnTo);
  const hero = useUiStore((s) => s.loginContext);
  const consumedRef = useRef(false);

  // 두 제공자의 실패는 한 화면에 하나만 보인다(마지막 시도가 덮는다). pending은 둘 다 잠근다.
  const failure = apple.failure ?? google.failure;
  const pending = google.pending || apple.pending;
  const clearFailure = () => {
    google.clearFailure();
    apple.clearFailure();
  };

  // 인앱 브라우저 → 외부 브라우저로 넘어온 경우 ?returnTo=로 복귀 경로를 이어받는다(1.5). 앱 내부 경로만 허용.
  useEffect(() => {
    const rt = params.get("returnTo");
    if (rt && rt.startsWith("/") && !rt.startsWith("//")) setReturnTo(rt);
  }, [params, setReturnTo]);

  // 웹 Google 리다이렉트 복귀(auth-session 3.9): ?googleHandle(일회용) → 세션, ?googleError → 실패 처리. 한 번만 쓰고 URL에서 지운다.
  const { exchangeGoogleHandle, failGoogleRedirect } = google;
  useEffect(() => {
    const handle = params.get("googleHandle");
    const error = params.get("googleError");
    if ((!handle && !error) || consumedRef.current) return;
    consumedRef.current = true;
    const rest = new URLSearchParams(params);
    rest.delete("googleHandle");
    rest.delete("googleError");
    setParams(rest, { replace: true });
    if (handle) void exchangeGoogleHandle(handle);
    else if (error) failGoogleRedirect(error);
  }, [params, setParams, exchangeGoogleHandle, failGoogleRedirect]);

  // iOS 는 Apple 버튼이 위, 그 외(Android·웹)는 Google 이 위
  const googleButton = <GoogleButton onClick={() => void google.signIn()} loading={google.pending} disabled={pending} />;
  const appleButton = <AppleButton onClick={() => void apple.signIn()} loading={apple.pending} disabled={pending} />;

  return (
    <main className="page auth">
      {/* 이전 화면 기록이 없으면(바로 /login 진입) 홈으로 닫는다 — PC 모달 dismissLogin 과 같은 규칙(AC0-245, QA #4) */}
      <TopNavigation tone="transparent" title="" right={null} onBack={() => backOr(navigate, "/home")} className="login__nav" />
      <div className={hero?.coverUrl ? "login__hero" : "login__hero login__hero--default"} data-testid="login-hero">
        <img src={hero?.coverUrl ?? defaultHeroUrl} alt="" className="login__cover" />
        <div className="login__copy">
          <img src={logoUrl} alt={t("app.name")} height={27} />
          <p className="login__headline">
            {hero?.tagline && <span className="login__tagline">{`"${hero.tagline}"`}</span>}
            <span>{t("login.cta")}</span>
          </p>
          {welcomeAmount !== undefined && <p className="login__gift">{t("login.gift", { amount: welcomeAmount })}</p>}
        </div>
      </div>
      <div className="auth__body login__body">
        {failure?.kind === "rejoinBlocked" && (
          <Callout
            tone="warning"
            title={t("login.rejoinBlocked", { days: rejoinDays ?? 0 })}
            description={failure.until ? t("login.rejoinBlocked.until", { until: formatDateTimeDot(failure.until) }) : undefined}
          />
        )}
        {platform.clientKind === "ios" ? (
          <>
            {appleButton}
            {googleButton}
          </>
        ) : (
          <>
            {googleButton}
            {appleButton}
          </>
        )}
        <p className="login__consent">{t("login.consent")}</p>
      </div>
      {/* F01-10(122:6824) 로그인 실패: neutral single [확인] */}
      <AlertDialog
        open={failure?.kind === "failed"}
        action="single"
        title={t("login.failed.title")}
        description={paragraphs(t("login.failed.body"))}
        primaryLabel={t("common.confirm")}
        onPrimary={clearFailure}
        onDismiss={clearFailure}
      />
      <AlertDialog
        open={failure?.kind === "accountBlocked"}
        tone="critical"
        action="stacked"
        title={t("blocked.account.title")}
        description={t("blocked.account.body")}
        primaryLabel={t("blocked.account.cta")}
        secondaryLabel={t("blocked.account.support")}
        onPrimary={clearFailure}
        onSecondary={() => {
          clearFailure();
          navigate("/support");
        }}
      />
    </main>
  );
}
